import { useEffect } from "react";
import get from "lodash/get";

// components
import notification from "../components/notifications";

// helpers
import useUserProfile from "./useUserProfile";
import campaigns from "@/api/query/campaigns";
import { handleCreateActivity } from "@/helpers/handleCreateActivity";
import { getSuccessMessage } from "@/containers/SaveToShortlistForm/SaveToShortListForm";

// interfaces
import { IBookingInfo } from "@/pageComponents/SingleSpacePage/helpers";
import { ICampaign, ICampaignItem } from "@/types/api/Campaign";


interface IUseAddToFavorite {
  isFavorite: boolean;
  bookingInfo?: IBookingInfo;
}

function useAddToFavorite({ isFavorite, bookingInfo }: IUseAddToFavorite) {
  const { data: userProfile } = useUserProfile();
  const { data, remove } = campaigns.useGetAllCampaigns({
    params: { limit: 40, status: 0, uniqKey: "useAddToFavorite" },
    config: { enabled: !!userProfile && userProfile.campaignsCount === 1 }
  });

  const campaignId = get(data, "results[0].id", 0) as number;

  const {
    mutateAsync: addSpaceToCampaign,
    isPending: isAddSpaceToCampaignPending
  } = campaigns.useAddSpaceToCampaign(campaignId);
  const {
    mutateAsync: deleteSpaceFromCampaign,
    isPending: isDeleteSpaceFromCampaignPending
  } = campaigns.useDeleteSpaceFromCampaign({
    id: campaignId,
    params: { modal: true }
  });

  const isOnlyOneCampaign = !!userProfile && userProfile.campaignsCount === 1;
  const isZeroCampaign = !!userProfile && userProfile.campaignsCount === 0;

  const handleToggleFavorite = async (
    { spaceId, onSuccessAddToCampaign }:
    { spaceId: number, onSuccessAddToCampaign?: () => void }
  ) => {
    if (!isOnlyOneCampaign) {
      return () => ({});
    }

    const funcToUse = isFavorite
      ? () => deleteSpaceFromCampaign(`${spaceId}?modal=true`).catch(() => notification({
        placement: "bottomLeft",
        duration: 5,
        actionText: "Go to campaign",
        actionProps: { href: `/campaign/${campaignId}/planner` },
        message: "Oops",
        description: "In order to remove this space from your favourites, please first cancel or delete all activities relating to this space on the campaign page.",
        type: "error",
      }))
      : () => addSpaceToCampaign({ space: spaceId })
        .then((res) => {
          !!onSuccessAddToCampaign && onSuccessAddToCampaign()

          notification({
            placement: "topRight",
            duration: 30,
            actionText: "Go to campaign",
            actionProps: { href: `/campaign/${campaignId}/planner` },
            message: getSuccessMessage(get(res, "data.space.name"), get(data, "results[0]") as unknown as ICampaign),
            type: "success",
          });

          return res;
        })
        .catch(() => notification({
          type: "error",
          placement: "bottomLeft",
          duration: 5,
          description: "Something went wrong, please try again later",
        }));

    const resp = await funcToUse() as { data: ICampaignItem } | { data: null };

    if (resp.data && bookingInfo && campaignId) {
      await handleCreateActivity(
        campaignId,
        resp.data.id,
        !!bookingInfo.start && !!bookingInfo.end
          ? { start: bookingInfo.start, end: bookingInfo.end, }
          : null
      ).catch(() => null);
    }
    
    return resp;
  };

  // effects
  useEffect(() => remove, []);

  return {
    handleToggleFavorite,
    isOnlyOneCampaign,
    isZeroCampaign,
    isLoading: isAddSpaceToCampaignPending || isDeleteSpaceFromCampaignPending
  };
}

export default useAddToFavorite;
